import { acceptCardReturn, saveCard, takeReturn, takeTrip, tripHome } from "./card"; // 1.5.42, 1.5.47; 1.5.63 acceptCardReturn, tripHome
import { idleWatch } from "./idle"; // 1.5.63
import { recordError } from "./diag"; // 1.5.63 — a card return refused goes to Lab
import { decodeAs, setMerchantTarget, tabContext } from "./handoff"; // 1.5.51; 1.5.55 tabContext
import { CircleArcAdapter } from "./adapters/CircleArcAdapter";
import { initTheme } from "./theme";
import { installSoundUnlock } from "./sound";
import { startFreshness } from "./freshness";
import type { WalletAdapter } from "./adapters/WalletAdapter";
import {
  assignName,
  getProfile,
  setProfile,
} from "./api";
import type { WalletName } from "./api";
import { canonicalizeCode, isWalletCode } from "./codes";
import { readFlagIdb, readFlagSync, setFlag } from "./deviceFlag";
import { saveCredential, type StoredCredential } from "./credentialStore";
import { tellPasskeyName } from "./passkeyName";
import { REVERT_KEY, type PrevWallet } from "./walletSwitch";
import { parseAmountSeg } from "./money";
import { reconcile } from "./settings";
import { getThemePref, setScreenTheme, setTheme } from "./theme";
import { isInAppBrowser, renderInAppGate } from "./inAppGate";
import { renderCreateView } from "./views/createView";
import { renderHomeView } from "./views/dropCatchView";
import type { SendPrefill } from "./sendReceiveFlow";
// v0.99.0 — "Pay with M®": a merchant checkout may send the payer here with
// ?back=<its page>; the success card then offers "Back to <merchant>". Only
// these origins are honoured — anything else is dropped silently.
const MERCHANT_BACK: { prefix: string; label: string }[] = [
  { prefix: "https://globalmoneyapp.com/fly/", label: "Omega Air" },
  { prefix: "https://mdrop.it/fly/", label: "Omega Air" }, // the short name; 301 to the line above
  { prefix: "https://fly.mdrop.it/", label: "Omega Air" },
  { prefix: "https://globalmoneyapp.com/cafe/", label: "Ondas Coffee" }, // 1.5.16 — the café demo (Worker 1.4.15)
  { prefix: "https://mdrop.it/cafe/", label: "Ondas Coffee" },
];
function merchantBack(raw: string | null): { url: string; label: string } | undefined {
  if (!raw) return undefined;
  const m = MERCHANT_BACK.find((b) => raw.startsWith(b.prefix));
  return m ? { url: raw, label: m.label } : undefined;
}
import { renderPayView } from "./views/payView";
import { renderEmbedPayView } from "./views/embedPayView"; // v0.99.9
import { renderDropClaimView } from "./views/dropClaimView";
import { askDisplayName } from "./views/dropShared";
import { renderDropConfirmView } from "./views/dropConfirmView";
import { SERIES, serialFromPath } from "./notes/serial";

// Single adapter behind the WalletAdapter seam (spec §4).
startFreshness(); // v1.0.12 — reload a stale app (iOS Chrome kept 1.0.5 for days)
// 1.5.10 — every tap that finds audio asleep wakes it (M's sounds; iOS needs the tap). 1.5.37 — not in M® Checkout's frame
// on a merchant's page: it plays "done" from an <audio> element (sound.ts), so its tap builds no audio context.
if (!location.pathname.startsWith("/embed/")) installSoundUnlock();
initTheme(); // v0.60.17 — resolves Pure/Midnight/Auto and arms the system listener for Auto

const adapter: WalletAdapter = new CircleArcAdapter();
const root = document.querySelector<HTMLElement>("#app")!;

let idbRecovered = false; // guard: IndexedDB flag-recovery runs at most once

// A payment the user began on a pay link and then chose "Go to my wallet" for —
// carried into the home Send flow so the amount/recipient/reference aren't lost.
let pendingPay: SendPrefill | null = null;

// 1.4.47 — the home is entered from a page where money just moved (a payment, a take, a Drop): its
// first statement read skips the caches, so the Balance panel's Recent transactions shows it.
let pendingFresh = false;

// An invite code from a deep link (mdrop.it/invite/CODE), carried into the
// create screen so it lands pre-filled. Consumed once, like pendingPay.
let pendingInvite: string | null = null;
// 1.4.37 — arrived at /join (betterthan.cash's "Got a code?"): the create view opens on the code screen.
let pendingJoin = false;
// 1.4.83 — arrived at /login (betterthan.cash's Login): the landing opens the phone's passkey sheet.
let pendingLogin = false;
// 1.4.88 — arrived at /add (the pay page's or a checkout's Add money, on a short balance): the home opens Add money.
let pendingAdd = false;
const takeAdd = () => { const a = pendingAdd; pendingAdd = false; return a; }; // consume once
let pendingCardSet = false; // 1.5.47 — back from the approver's enrolment: Settings → M® card open, "Your M® card is set."
const takeCardSet = () => { const a = pendingCardSet; pendingCardSet = false; return a; };

// --- v0.20.10 auto-logout: return to the lock screen after inactivity --------
// "Logged in" here just means the home view is mounted (no server session). So logging out = tearing the home down and
// showing "Welcome back" (the device flag stays, so it's login-only and re-entry needs Face ID). 1.5.63 — the watch
// lives in idle.ts (tested there): a tap after the 3 minutes logs out instead of restarting the clock, and a page
// restored from the cache, or focused again, is checked at once.
const idle = idleWatch(() => showCreate(), { win: window, doc: document, now: () => Date.now() });
const startIdle = () => idle.start();
const stopIdle = () => idle.stop();

// 1.4.21 (Alexander, 16 Oct: "it took a couple of seconds to show the keypad —
// always show the keypad and the welcome right away") — what this browser knows
// about the wallet it last opened: its name (code, link) and the first name on
// its profile. With it, a login renders the home straight after Face ID and the
// Worker's round trips (name, profile, settings) run behind it; without it
// (a new wallet, a first login in this browser) the old path, which ends by
// writing it. Device-only; holds nothing private beyond the greeting's name.
const HOME_KEY = "m.home";
interface HomeCache { a: string; name: WalletName; greet?: string }
function readHomeCache(address: string): HomeCache | null {
  try {
    const j = JSON.parse(localStorage.getItem(HOME_KEY) ?? "null") as HomeCache | null;
    return j?.a?.toLowerCase() === address.toLowerCase() && j.name?.slug ? j : null;
  } catch { return null; }
}
function writeHomeCache(address: string, name: WalletName | null, greet?: string): void {
  if (!name) return;
  try {
    const was = readHomeCache(address);
    localStorage.setItem(HOME_KEY, JSON.stringify({ a: address, name, greet: greet ?? was?.greet }));
  } catch { /* ignore */ }
}
function syncProfileSettings(p: { settings?: unknown; name?: string | null }, address: string): void {
  // 1.5.44 — the passkey's name follows the wallet's (passkeyName.ts): told once per name, so this
  // is free after the first time, and catches a rename made on another device at the next login.
  void tellPasskeyName(address, p.name);
  const { push, changed } = reconcile(p.settings as Parameters<typeof reconcile>[0]);
  if (changed.includes("theme")) setTheme(getThemePref());
  if (changed.includes("mask")) window.dispatchEvent(new Event("m:mask")); // 1.4.67 — the home repaints its pill
  if (push) void setProfile({ address, settings: push }).catch(() => {});
}

async function toHome(address: string, desiredSlug?: string, givenName?: string | null) {
  // 1.4.72 — the code screen's Pure ends at the wallet: the person's theme from here — set as the home paints
  // (below), not here: the Worker's calls in between had shown the last screen in Dusk (Alexander's video, 21 Oct).
  const freshStatement = pendingFresh;
  pendingFresh = false; // consume once
  const firstLogin = !readHomeCache(address); // 1.4.48 — this wallet's first home in this browser
  const cached = !desiredSlug && !givenName ? readHomeCache(address) : null;
  if (cached) {
    setFlag(cached.name.display);
    const sendPrefill = pendingPay ?? undefined;
    pendingPay = null;
    setScreenTheme(null);
    renderHomeView(root, { adapter, address, name: cached.name, onExit: () => showCreate(), sendPrefill, freshStatement, firstLogin, openAdd: takeAdd(), openCardSet: takeCardSet() });
    startIdle();
    // Behind the home: the name row (idempotent) and the profile's settings.
    void assignName(address).then((n) => writeHomeCache(address, n)).catch(() => {});
    void getProfile(address).then((p) => syncProfileSettings(p, address)).catch(() => {});
    return;
  }
  let name: WalletName | null = null;
  try {
    name = await assignName(address, desiredSlug);
  } catch (e) {
    console.warn("[M] name assign failed (non-blocking):", e);
  }
  setFlag(name?.display ?? null); // remember: this browser now has a wallet
  // v1.1.28 — the name typed at creation (the passkey's name too) is saved
  // HERE: the profile lives on the wallet's name row, which only exists once
  // assignName has run — saved any earlier, the Worker answered no_wallet and
  // the home asked "What shall we call you?" a second time.
  // 1.4.75 — a wallet just made has its name in hand: the home paints now, the profile is saved and read behind it,
  // and "Hi <name>." comes from this browser's record (m.home), not from a read that may beat the save.
  if (givenName) {
    void setProfile({ address, name: givenName }).catch(() => {})
      .then(() => getProfile(address)).then((p) => syncProfileSettings(p, address)).catch(() => {});
  }
  // v0.70.19 — a wallet from before names were required: ask once, then on.
  if (!givenName) try {
    const p = await getProfile(address);
    // v0.80.40 — the wallet's settings ride this same response: merge them into
    // this browser (a new one or a fresh Home Screen install starts empty and
    // simply inherits them), and push back if this device is ahead. Theme is
    // re-applied by hand because it was painted from localStorage at boot.
    syncProfileSettings(p, address);
    if (!p.name && !givenName) {
      setScreenTheme(null);
      const n = await askDisplayName(root);
      await setProfile({ address, name: n }).catch(() => {});
    }
  } catch {
    /* offline: don't block login on it */
  }
  writeHomeCache(address, name, givenName ? givenName.trim().split(/\s+/)[0] : undefined); // 1.4.21 — the next login renders at once
  const sendPrefill = pendingPay ?? undefined;
  pendingPay = null; // consume once
  setScreenTheme(null);
  renderHomeView(root, {
    adapter,
    address,
    name,
    onExit: () => showCreate(),
    sendPrefill,
    freshStatement,
    firstLogin,
    justMade: !!givenName, // 1.4.75 — made a moment ago: the pill opens on 0, not ••••
    openAdd: takeAdd(), // 1.4.88
    openCardSet: takeCardSet(), // 1.5.47
  });
  startIdle(); // arm inactivity auto-logout while on the home screen
}

/** v0.60.1 (S10) — launched from a Home-Screen icon (separate storage partition). */
function isStandalone(): boolean {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)").matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

// v1.1.9 — "No, keep 🐼": at the lock screen the browser goes back to the
// wallet it opened with before (its state restored, this one's put aside —
// nothing deleted), so the next login shows 🐼 and Face ID asks for its passkey.
function applyRevert(): void {
  try {
    const raw = localStorage.getItem(REVERT_KEY);
    if (!raw) return;
    localStorage.removeItem(REVERT_KEY);
    const prev = JSON.parse(raw) as PrevWallet;
    const cred = prev.cred as StoredCredential | null;
    if (!cred?.id || !cred.address) return;
    saveCredential(cred, { ask: false });
    setFlag(prev.name);
  } catch {
    /* storage blocked — nothing to revert */
  }
}

function showCreate(errorMessage?: string) {
  stopIdle(); // leaving the home screen — no auto-logout on the lock screen
  applyRevert();
  const remembered = readFlagSync();
  const invitePrefill = pendingInvite ?? undefined;
  pendingInvite = null; // consume once
  const openCode = pendingJoin;
  pendingJoin = false; // consume once
  const openLogin = pendingLogin;
  pendingLogin = false; // consume once
  renderCreateView(root, {
    adapter,
    remembered,
    invitePrefill,
    openCode,
    openLogin,
    loginFirst: isStandalone(),
    onReady: (address, desiredSlug, givenName) => void toHome(address, desiredSlug, givenName),
    onError: (message) => {
      showCreate();
      const s = root.querySelector<HTMLElement>("#status");
      if (s) s.textContent = message;
    },
  });
  if (errorMessage) {
    const s = root.querySelector<HTMLElement>("#status");
    if (s) s.textContent = errorMessage;
  }

  // If localStorage lost the flag but IndexedDB still has it, restore and
  // re-render as "welcome back" (the guard survives one store being cleared).
  if (!remembered.has && !idbRecovered) {
    idbRecovered = true;
    readFlagIdb()
      .then((idb) => {
        if (idb.has && !readFlagSync().has) {
          setFlag(idb.name);
          showCreate();
        }
      })
      .catch(() => {});
  }
}

/**
 * v0.30 — Kill iOS Safari pull-to-refresh (which reloads the page and logs the
 * user out). `overscroll-behavior` is unreliable on iOS, so we cancel only the
 * one offending gesture: a downward drag that begins while the page is already
 * scrolled to the very top. Everything else — normal scrolling, sheet drags,
 * the keypad — is untouched, because we only preventDefault when the page can't
 * scroll up any further AND the finger is moving down.
 */
function installPullToRefreshGuard() {
  let startY = 0;
  document.addEventListener(
    "touchstart",
    (e) => {
      startY = e.touches[0]?.clientY ?? 0;
    },
    { passive: true },
  );
  document.addEventListener(
    "touchmove",
    (e) => {
      const target = e.target as HTMLElement | null;
      // Never interfere with a range slider (or other interactive controls):
      // the guard's preventDefault() on a downward drag was cancelling the
      // slider's own drag, making it feel sticky. Let these handle their own
      // touch entirely.
      if (
        target?.closest(
          'input[type="range"], .earn-trk, button, a, [role="slider"]',
        )
      ) {
        return;
      }
      const y = e.touches[0]?.clientY ?? 0;
      const movingDown = y > startY;
      const atTop =
        (window.scrollY || document.documentElement.scrollTop || 0) <= 0;
      // Only when the whole page is at the top and the drag is downward — the
      // exact precondition for pull-to-refresh. A scrollable element inside
      // (e.g. an open sheet) scrolls normally because it isn't at document top,
      // or handles its own overscroll containment.
      if (atTop && movingDown) {
        const scroller = target?.closest(".sheet");
        // If the touch is inside a sheet that can still scroll up, let it.
        if (!scroller || (scroller as HTMLElement).scrollTop <= 0) {
          e.preventDefault();
        }
      }
    },
    { passive: false },
  );
}

function boot() {
  installPullToRefreshGuard();
  // 1.5.42 — back from the approver's enrolment (Settings → M® card → Set up): the card's credential, kept for its wallet
  // 1.5.47 — and back the same way: the home reopens for the wallet that left (no second Face ID within the idle logout's
  // 3 minutes), Settings → M® card open on "Your M® card is set."; a cancelled enrolment (#x=cancel) comes home too,
  // its fragment cleared (it had stayed in the address bar, and the home had opened on the login — the recording of 31 Oct).
  // 1.5.63 — a returned card is saved only for this tab's own trip (under 5 min old, the same one-time code, the same
  // wallet): a key tapped on an abandoned setup screen, or a crafted #k= link, sets nothing (Lab: card/enrol). The
  // home without Face ID stays within the trip's first 3 minutes (M's idle logout), as before.
  let cardBack: string | null = null;
  let tripAddress: string | null = null;
  if (/^#(k=|x=)/.test(location.hash)) {
    const r = takeReturn();
    const trip = takeTrip();
    if (r && "card" in r) {
      const why = acceptCardReturn(r.card, trip);
      if (why) recordError("card/enrol", new Error(`${why} · card ${String(r.card.id ?? "").slice(0, 5)} · for ${String(r.card.address ?? "").slice(0, 6)}`));
      else { saveCard(r.card); cardBack = "set"; }
    } else if (r && "refused" in r) cardBack = "cancel";
    tripAddress = cardBack ? tripHome(trip) : null;
  }
  const early = window.location.pathname.split("/").filter(Boolean);
  const kind = (early[0] ?? "").toLowerCase();
  // v1.1.18 — a note's link: mdrop.it/A0000040Z5 (the Take page). Tested
  // BEFORE the wallet code: canonicalising a code drops 0 and 1, which can
  // shrink a serial to eight symbols (brief §8.2). The serial must be the whole
  // path, pass its check symbol and name a series this build knows.
  const noteSerial = serialFromPath(window.location.pathname);
  const takeSerial = noteSerial && SERIES[noteSerial.series] ? noteSerial : null;
  // In-app webviews block camera + passkeys — stop before login and steer out.
  // 1.4.37 (Alexander, 17 Oct) — no in-app browser gets past this, Snapchat included: its
  // browser refused to create a passkey (NotAllowedError) and signed in only sometimes, and
  // copying the link into Safari takes a second. The 1.4.31–1.4.36 test switch is gone.
  if (isInAppBrowser()) {
    renderInAppGate(root, {
      kind: takeSerial ? "note" : kind === "d" && early[1] ? "drop" : kind === "c" && early[1] ? "claimed" : kind === "invite" && early[1] ? "invite" : "plain",
      serial: takeSerial,
    });
    return;
  }
  // v0.70 — Drops. /d/{id} = claim (recipient); /c/{id} = confirm/release (sender).
  if ((kind === "d" || kind === "c") && early[1]) {
    const id = early[1];
    const deps = {
      adapter,
      dropId: id,
      by: early[2], // v0.90.21 — /c/{id}/{code}: the claimer's code, from "Tell Bob"
      onGoHome: () => {
        history.pushState({}, "", "/");
        showCreate();
      },
      onEnterAs: (address: string) => { pendingFresh = true; void toHome(address); },
    };
    if (kind === "d") renderDropClaimView(root, deps);
    else renderDropConfirmView(root, deps);
    return;
  }
  if (takeSerial) {
    // The view (and the take's signing code) loads only for a note's link.
    void import("./views/noteTakeView").then(({ renderNoteTakeView }) =>
      renderNoteTakeView(root, {
        adapter,
        serial: takeSerial,
        onGoHome: () => {
          history.pushState({}, "", "/");
          showCreate();
        },
        onEnterAs: (address: string) => {
          history.pushState({}, "", "/");
          pendingFresh = true; // 1.4.47 — the take's money is in: the first statement read is fresh
          void toHome(address);
        },
      }),
    );
    return;
  }
  // v0.99.9 — /embed/pay?to=CODE&amount=&ccy=&ref= : the slider inside a
  // merchant's page (an iframe mounted by /m.js). Pure + guilloche always.
  if (kind === "embed" && (early[1] ?? "").toLowerCase() === "pay") {
    const q = new URLSearchParams(location.search);
    const to = canonicalizeCode(q.get("to") ?? "");
    const amount = Number(q.get("amount"));
    document.documentElement.setAttribute("data-theme", "light");
    document.documentElement.classList.add("embedded");
    if (!isWalletCode(to) || !(amount > 0)) {
      root.innerHTML = `<main class="screen embed"><p class="status">This payment link is incomplete.</p></main>`;
      return;
    }
    renderEmbedPayView(root, { adapter, to, amount, currency: q.get("ccy") ?? "USD", reference: q.get("ref") ?? undefined });
    return;
  }
  // Path shapes:
  //   /{CODE}                       → open pay (payer picks the amount)
  //   /{CODE}/USD{amount}           → request: amount pre-filled + locked
  //   /{CODE}/USD{amount}/{ref}     → + reference
  // The code is case/hyphen-agnostic; amount matches case-insensitively; the
  // reference keeps its case.
  const segs = window.location.pathname
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
  const first = segs[0] ?? "";

  // 1.5.47 — back from the approver's enrolment: the same wallet's home, Settings open on the M® card
  if (!first && tripAddress) {
    pendingCardSet = cardBack === "set";
    history.replaceState({}, "", "/");
    void toHome(tripAddress);
    return;
  }

  // /invite/CODE → create screen, code pre-filled. The gate check + welcome-back
  // handling live in createView (a returning user never sees the code wall).
  if (first.toLowerCase() === "invite" && segs[1]) {
    pendingInvite = segs[1];
    history.replaceState({}, "", "/");
    showCreate();
    return;
  }
  // 1.4.37 — globalmoneyapp.com/join: where betterthan.cash's "Got a code?" leads.
  if (first.toLowerCase() === "join" && !segs[1]) {
    pendingJoin = true;
    history.replaceState({}, "", "/");
    showCreate();
    return;
  }
  // 1.4.88 — globalmoneyapp.com/add: a short balance's Add money (the pay page in this tab, a checkout in a new one).
  if (first.toLowerCase() === "add" && !segs[1]) {
    pendingAdd = true;
    history.replaceState({}, "", "/");
    showCreate();
    return;
  }
  // 1.4.83 — globalmoneyapp.com/login: where betterthan.cash's Login leads.
  if (first.toLowerCase() === "login" && !segs[1]) {
    pendingLogin = true;
    history.replaceState({}, "", "/");
    showCreate();
    return;
  }

  const slug = canonicalizeCode(first);
  if (isWalletCode(slug)) {
    let lockedAmount: number | undefined;
    let lockedCurrency: string | undefined;   // v0.80.29 — the link's own currency
    let reference: string | undefined;
    let malformed = false;
    if (segs[1]) {
      const parsed = parseAmountSeg(segs[1]);
      const amt = parsed?.amount ?? null;
      lockedCurrency = parsed?.currency;
      if (amt == null) {
        // Name is valid but the amount segment is broken (typo, truncated).
        malformed = true;
      } else {
        lockedAmount = amt;
        if (segs[2]) {
          try {
            reference = decodeURIComponent(segs[2]);
          } catch {
            reference = segs[2];
          }
        }
      }
    }
    // 1.5.27 (Alexander, 27 Oct) — opened by M® Checkout's "Pay with M® ↗": M's world, Midnight Cobalt, whatever the
    // person's theme (the screen's, not the preference).
    const handoff = new URLSearchParams(location.search).get("handoff") === "1";
    if (handoff) setScreenTheme("dark");
    // 1.5.55 — M's tab opened by the Pay with M® button on a merchant's page: the site and the page to tell are the ones the
    // browser says opened the tab (the referrer), kept for the tab across the M® card's trip; ?site= only without one.
    const siteParam = new URLSearchParams(location.search).get("site");
    let tabCtx: { site?: string; target: string } | null = null;
    if (handoff) { let ss: Storage | null = null; try { ss = sessionStorage; } catch { /* refused */ } tabCtx = tabContext(document.referrer, location.origin, siteParam, ss, location.pathname); setMerchantTarget(tabCtx.target); }
    renderPayView(root, {
      adapter,
      recipientSlug: slug,
      lockedAmount,
      lockedCurrency,
      reference,
      malformed,
      merchantBack: merchantBack(new URLSearchParams(location.search).get("back")),
      scanned: new URLSearchParams(location.search).get("qr") === "1", // 1.5.17
      site: tabCtx ? tabCtx.site : siteParam?.slice(0, 300) || undefined, // 1.5.21 — checked with the Worker; 1.5.55 a tab's from its opener
      handoff, // 1.5.27 — M's tab for M® Checkout's first payment at a merchant
      as: decodeAs(new URLSearchParams(location.search).get("as")), // 1.5.51 — the wallet the frame picked, whose card is taken here
      pick: handoff && new URLSearchParams(location.search).get("pick") === "1", // 1.5.53 — the frame's "Choose another M® wallet"
      onGoHome: (prefill) => {
        pendingPay = prefill ?? null; // preserve the payment for the wallet
        history.pushState({}, "", "/");
        showCreate();
      },
      onPaidAs: (address) => { pendingFresh = true; void toHome(address); },
    });
  } else {
    if (segs.length) history.replaceState({}, "", "/");
    showCreate();
  }
}

boot();
